import { memo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Slider from "react-slick";
import styles from "./index.module.sass";
import { NextArrow, PrevArrow } from "../../components/sliderArrow";

function SliderView({ list }) {
  const sliderRef = useRef(null);
  const containerRef = useRef(null);

  const parseYouTubeId = (url) => {
    if (!url) return null;
    try {
      const u = new URL(url);
      if (u.hostname.includes("youtu.be")) {
        return u.pathname.replace("/", "");
      }
      if (u.hostname.includes("youtube.com")) {
        if (u.pathname === "/watch") {
          return u.searchParams.get("v");
        }
        const parts = u.pathname.split("/");
        const idx = parts.indexOf("embed");
        if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
      }
    } catch (e) {
      // ignore
    }
    const m = url.match(/(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]{6,})/);
    return m ? m[1] : null;
  };

  const getYouTubeIframeByPosition = (position) => {
    return (
      document.getElementById(`yt-iframe-${position}`) ||
      document.querySelector(`iframe[title="yt-${position}"]`)
    );
  };

  const postMessageToYouTube = (iframe, func, args = []) => {
    try {
      if (!iframe || !iframe.contentWindow) return;
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func, args, id: iframe.id || undefined }),
        "*"
      );
    } catch {
      // ignore
    }
  };
  const registerYouTubeEvents = (iframe) => {
    try {
      if (!iframe || !iframe.contentWindow) return;
      // 要先宣告正在監聽，YT 才會回傳事件
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "listening", id: iframe.id || undefined }),
        "*"
      );
      // 訂閱 onStateChange
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func: "addEventListener", args: ["onStateChange"], id: iframe.id || undefined }),
        "*"
      );
    } catch {
      // ignore
    }
  };

  const handleVideoPlay = () => {
    sliderRef.current.slickPause();
  };

  const handleVideoPause = () => {
    sliderRef.current.slickPlay();
  };

  const handleVideoEnd = () => {
    sliderRef.current.slickPlay();
  };

  const handleSliderBeforeChange = (oldIndex, newIndex) => {
    const prevSlide = list[oldIndex];
    const currentSlide = list[newIndex];
    // 如果上一個投影片是影片，則暫停
    if (prevSlide.fileType === "video") {
      const videoElement = document.getElementById("slideVideo" + prevSlide.position);
      if (videoElement) {
        videoElement.pause();
      }
    }
    // 如果上一個投影片是 YouTube，則暫停並恢復輪播
    if (prevSlide.fileType === "youtube") {
      const prevIframe = getYouTubeIframeByPosition(prevSlide.position);
      if (prevIframe) {
        postMessageToYouTube(prevIframe, "pauseVideo");
      }
      if (sliderRef.current && typeof sliderRef.current.slickPlay === "function") {
        sliderRef.current.slickPlay();
      }
    }

    if (currentSlide.fileType === "video") {
      const videoElement = document.getElementById("slideVideo" + currentSlide.position);
      if (videoElement) {
        if (document.visibilityState === "visible") {
          const p = videoElement.play();
          if (p && typeof p.catch === "function") {
            p.catch(() => undefined);
          }
        }
      }
    }
  };

  const handleSliderAfterChange = (currentIndex) => {
    const currentSlide = list[currentIndex];
    if (!currentSlide) return;
    if (document.visibilityState !== "visible") return;

    if (currentSlide.fileType === "youtube") {
      const iframe = getYouTubeIframeByPosition(currentSlide.position);
      if (iframe) {
        // 靜音並播放，以符合自動播放政策
        registerYouTubeEvents(iframe);
        postMessageToYouTube(iframe, "mute");
        postMessageToYouTube(iframe, "playVideo");
        // 播放影片時暫停輪播，避免自動切走
        if (sliderRef.current && typeof sliderRef.current.slickPause === "function") {
          sliderRef.current.slickPause();
        }
      }
    }
  };

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        if (sliderRef.current && typeof sliderRef.current.slickPause === "function") {
          sliderRef.current.slickPause();
        }
        if (containerRef.current) {
          const videos = containerRef.current.querySelectorAll("video");
          videos.forEach((v) => {
            try { v.pause(); } catch (e) { void e; }
          });
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // 初始載入時，若第一張為影片或 YouTube，嘗試自動播放
  useEffect(() => {
    if (!Array.isArray(list) || list.length === 0) return;
    if (document.visibilityState !== "visible") return;
    const first = list[0];
    // 稍等 DOM 與 iframe 載入
    const timer = setTimeout(() => {
      if (first.fileType === "video") {
        const videoElement = document.getElementById("slideVideo" + first.position);
        if (videoElement) {
          try {
            const p = videoElement.play();
            if (p && typeof p.catch === "function") p.catch(() => undefined);
          } catch (e) { void e; }
        }
      } else if (first.fileType === "youtube") {
        const iframe = getYouTubeIframeByPosition(first.position);
        if (iframe) {
          try {
            registerYouTubeEvents(iframe);
            postMessageToYouTube(iframe, "mute");
            postMessageToYouTube(iframe, "playVideo");
            if (sliderRef.current && typeof sliderRef.current.slickPause === "function") {
              sliderRef.current.slickPause();
            }
          } catch (e) { void e; }
        }
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [list]);

  // 監聽 YouTube 播放狀態，結束後自動切到下一張
  useEffect(() => {
    const onMessage = (event) => {
      let data = null;
      try {
        data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        data = null;
      }
      if (!data) return;
      // 支援 onStateChange 與 infoDelivery(playerState)
      const isEnded =
        (data.event === "onStateChange" && data.info === 0) ||
        (data.event === "infoDelivery" && data.info && typeof data.info.playerState !== "undefined" && data.info.playerState === 0);
      if (!isEnded) return;
      if (!containerRef.current) return;
      const iframes = containerRef.current.querySelectorAll('iframe[src*="youtube.com/embed"]');
      let isFromHere = false;
      iframes.forEach((ifr) => {
        if (isFromHere) return;
        try {
          if (ifr.contentWindow === event.source) isFromHere = true;
        } catch (e) { void e; }
      });
      if (!isFromHere) return;
      if (sliderRef.current) {
        if (typeof sliderRef.current.slickNext === "function") sliderRef.current.slickNext();
        if (typeof sliderRef.current.slickPlay === "function") sliderRef.current.slickPlay();
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  return (
    <div ref={containerRef}>
      <Slider
        ref={sliderRef}
        dots={true}
        infinite={true}
        slidesToShow={1}
        slidesToScroll={1}
        autoplay={true}
        pauseOnHover={true}
        pauseOnFocus={true}
        beforeChange={handleSliderBeforeChange}
        afterChange={handleSliderAfterChange}
        prevArrow={<PrevArrow customClassName={styles.prevArrow} />}
        nextArrow={<NextArrow customClassName={styles.nextArrow} />}
      >
        {list.map((item) => (
          <div key={item.position} className={"aspect-[1060/596] relative"}>
            {item.fileType === "image" && (
              <div
                className="w-full h-full bg-black bg-contain bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${item.fileUrl})` }}
              />
            )}
            {item.fileType === "video" && (
              <div className="w-full h-full" key={item.position}>
                <video
                  id={"slideVideo" + item.position}
                  className="w-full h-full object-contain bg-black"
                  controls
                  muted
                  playsInline
                  onLoadedMetadata={(e) => {
                    e.currentTarget.volume = 0.5;
                  }}
                  onPlay={handleVideoPlay}
                  onPause={handleVideoPause}
                  onEnded={handleVideoEnd}
                >
                  <source src={item.fileUrl} type={`video/mp4`} />
                </video>
              </div>
            )}
            {item.fileType === "youtube" && (
              <div className="w-full h-full bg-black">
                <iframe
                  id={`yt-iframe-${item.position}`}
                  title={`yt-${item.position}`}
                  className="w-full h-full"
                  onLoad={(e) => {
                    const iframe = e.currentTarget;
                    registerYouTubeEvents(iframe);
                  }}
                  src={(function () {
                    const id = parseYouTubeId(item.fileUrl || "");
                    const base = id ? `https://www.youtube.com/embed/${id}` : item.fileUrl;
                    const origin =
                      typeof window !== "undefined" && window.location && window.location.origin
                        ? `&origin=${encodeURIComponent(window.location.origin)}`
                        : "";
                    // 啟用 JS API 以便透過 postMessage 控制播放/暫停
                    return `${base}?rel=0&playsinline=1&enablejsapi=1${origin}`;
                  })()}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            )}
          </div>
        ))}
      </Slider>
    </div>
  );
}

SliderView.propTypes = {
  list: PropTypes.arrayOf(
    PropTypes.shape({
      position: PropTypes.number.isRequired,
      fileType: PropTypes.oneOf(["image", "video", "youtube"]).isRequired,
      fileUrl: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default memo(SliderView);